import { MessageContext } from 'contexts/MessageContext';
import React, { memo, useContext, useMemo } from 'react';

import {
  type IAction,
  type IMessageElement,
  type IStep
} from '@chainlit/react-client';

import BlinkingCursor from '@/components/BlinkingCursor';

import { Message } from './Message';
import ToolWorkflow from './ToolWorkflow';

interface Props {
  messages: IStep[];
  elements: IMessageElement[];
  actions: IAction[];
  indent: number;
  isRunning?: boolean;
  scorableRun?: IStep;
}

const CL_RUN_NAMES = ['on_chat_start', 'on_message', 'on_audio_end'];

const hasActiveToolStep = (step: IStep): boolean => {
  return (
    step.steps?.some(
      (s) =>
        (s.type === 'tool' && s.start && !s.end && !s.isError) ||
        s.type.includes('message') ||
        hasActiveToolStep(s)
    ) || false
  );
};

const hasAssistantMessage = (step: IStep): boolean => {
  return (
    step.steps?.some(
      (s) => s.type === 'assistant_message' || hasAssistantMessage(s)
    ) || false
  );
};

const Messages = memo(
  ({ messages, elements, actions, indent, isRunning, scorableRun }: Props) => {
    const messageContext = useContext(MessageContext);

    const lastAssistantMessage = useMemo(() => {
      return messages.findLast((m) => m.type === 'assistant_message');
    }, [messages]);

    const lastScorableAssistantMessage = useMemo(() => {
      return scorableRun?.steps?.findLast(
        (m) => m.type === 'assistant_message'
      );
    }, [scorableRun]);

    // 同一层级连续的工具步骤聚合为一张「处理过程」卡，替代逐条折叠行。
    // 工具步骤的 message 型子步骤（如K线图「产物」消息）提级到根层级、渲染在卡片之后（卡外）；
    // 带 message 之外子步骤的工具不聚合，回退原渲染（cot=hidden 时也维持原行为）。
    const groupable = (m: IStep) =>
      m.type === 'tool' &&
      messageContext.cot !== 'hidden' &&
      (m.steps || []).every((s) => s.type.includes('message'));

    const renderNodes: React.ReactNode[] = [];
    let pendingTools: IStep[] = [];
    const flushPendingTools = () => {
      if (!pendingTools.length) return;
      const childMessages = pendingTools.flatMap((s) =>
        (s.steps || []).filter((c) => c.type.includes('message'))
      );
      renderNodes.push(
        <ToolWorkflow
          key={`tool-workflow-${pendingTools[0].id}`}
          steps={pendingTools}
        />
      );
      if (childMessages.length) {
        renderNodes.push(
          <Messages
            key={`tool-workflow-children-${pendingTools[0].id}`}
            messages={childMessages}
            elements={elements}
            actions={actions}
            indent={0}
            isRunning={isRunning}
          />
        );
      }
      pendingTools = [];
    };

    messages.forEach((m) => {
      if (groupable(m)) {
        pendingTools.push(m);
        return;
      }
      flushPendingTools();
      // Handle chainlit runs
      if (CL_RUN_NAMES.includes(m.name)) {
        const isRunning = !m.end && !m.isError && messageContext.loading;
        const isToolCallCoT =
          messageContext.cot === 'tool_call' ||
          messageContext.cot === 'full';
        const isHiddenCoT = messageContext.cot === 'hidden';

        const showToolCoTLoader = isToolCallCoT
          ? isRunning && !hasActiveToolStep(m)
          : false;

        const showHiddenCoTLoader = isHiddenCoT
          ? isRunning && !hasAssistantMessage(m)
          : false;
        // Ignore on_chat_start for scorable run
        const scorableRun =
          !isRunning && m.name !== 'on_chat_start' ? m : undefined;
        renderNodes.push(
          <React.Fragment key={m.id}>
            {m.steps?.length ? (
              <Messages
                messages={m.steps}
                elements={elements}
                actions={actions}
                indent={indent}
                isRunning={isRunning}
                scorableRun={scorableRun}
              />
            ) : null}
            {(showToolCoTLoader || showHiddenCoTLoader) &&
            m.name !== 'on_chat_start' ? (
              <BlinkingCursor />
            ) : null}
          </React.Fragment>
        );
      } else {
        // Score the current run
        const _scorableRun = m.type === 'run' ? m : scorableRun;
        // The message is scorable if it is the last assistant message of the run

        const isRunLastAssistantMessage =
          m.type === 'run' ? false : m === lastScorableAssistantMessage;

        const isLastAssistantMessage = m === lastAssistantMessage;

        const isScorable =
          isRunLastAssistantMessage || isLastAssistantMessage;

        renderNodes.push(
          <Message
            message={m}
            elements={elements}
            actions={actions}
            key={m.id}
            indent={indent}
            isRunning={isRunning}
            scorableRun={_scorableRun}
            isScorable={isScorable}
          />
        );
      }
    });
    flushPendingTools();

    return <>{renderNodes}</>;
  }
);

export { Messages };
